import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { FillPill } from "@/components/app/patterns/fill-pill";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { RelativeTime } from "@/components/app/patterns/relative-time";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare } from "@/lib/icons";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useEveryApplication } from "./-components/use-every-application";
import { ConversationThread } from "./-components/conversation-thread";

type Conversation = Output<typeof orpcClient.workforce.offers.conversations>[number];

function conversationColumns(stageOf: ReadonlyMap<string, string>): ColumnDef<Conversation>[] {
  return [
    {
      id: "candidate",
      header: "Candidate",
      enableHiding: false,
      accessorFn: (row) => row.applicantName || "Unnamed",
      cell: ({ row: { original: row } }) => (
        <PersonCell
          name={row.applicantName || "Unnamed"}
          renderLink={(name) => (
            <Link
              to="/hiring/applications/$applicationId"
              params={{ applicationId: row.applicationId }}
              className="focus-ring -my-1 min-w-0 truncate rounded-sm py-1 hover:underline"
            >
              {name}
            </Link>
          )}
          badge={
            row.unread > 0 ? (
              <FillPill tone="warn">
                {row.unread === 1 ? "1 unread" : `${row.unread} unread`}
              </FillPill>
            ) : null
          }
        />
      ),
    },
    {
      id: "job",
      header: "Job",
      accessorFn: (row) => row.jobTitle ?? "-",
      meta: { filter: "select", clamp: "md", className: "text-muted-foreground" },
    },
    {
      id: "stage",
      header: "Stage",
      accessorFn: (row) => stageOf.get(row.applicationId) ?? "-",
      cell: ({ row: { original: row } }) =>
        stageOf.get(row.applicationId) ?? <span className="text-muted-foreground">-</span>,
      meta: { filter: "select", priority: 2 },
    },
    {
      id: "last",
      header: "Last message",
      accessorFn: (row) => new Date(row.lastMessageAt).getTime(),
      cell: ({ row: { original: row } }) => <RelativeTime value={row.lastMessageAt} />,
      meta: { nowrap: true },
    },
  ];
}

export const Route = createFileRoute("/_authenticated/_workforce/hiring/inbox")({
  component: RecruiterInbox,
});

/** One row per candidate who has been in touch, newest first, so a recruiter can see who is waiting
 * on an answer and where they are in the pipeline. */
function RecruiterInbox() {
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);

  const conversations = useQuery(
    orpc.workforce.offers.conversations.queryOptions({ input: { facilityId } }),
  );
  const applications = useEveryApplication({ facilityId });

  const markRead = useMutation(
    orpc.workforce.offers.markRead.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.workforce.offers.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = conversations.data ?? [];
  const open = rows.find((row) => row.applicationId === openId);
  const waiting = rows.filter((row) => row.unread > 0).length;
  const stageOf = new Map((applications.data?.rows ?? []).map((row) => [row.id, row.stageLabel]));

  const toggle = (applicationId: string, unread: number) => {
    if (openId === applicationId) {
      setOpenId(null);
      return;
    }
    setOpenId(applicationId);
    if (unread > 0) markRead.mutate({ applicationId });
  };

  const meta = conversations.isPending
    ? "Loading…"
    : rows.length === 0
      ? "No candidate has messaged you yet."
      : waiting
        ? `${waiting} waiting on a reply`
        : "Nobody is waiting on a reply.";

  return (
    <>
      <PageHeader
        title="Conversations"
        count={conversations.data ? rows.length : undefined}
        meta={meta}
      />

      {conversations.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : conversations.isError ? (
        <ReadFailed
          message="Could not load your conversations. This is not a statement that nobody is waiting."
          error={conversations.error}
          onRetry={() => void conversations.refetch()}
          retrying={conversations.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No conversations yet"
          description="Candidates appear here as soon as they message the recruiting line."
        />
      ) : (
        <>
          {open ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
                <CardTitle asChild>
                  <h2>{open.applicantName || "Unnamed"}</h2>
                </CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setOpenId(null)}>
                  Hide
                </Button>
              </CardHeader>
              <CardContent>
                <ConversationThread applicationId={open.applicationId} />
              </CardContent>
            </Card>
          ) : null}
          <DataTable
            data={rows}
            columns={conversationColumns(stageOf)}
            noun="conversations"
            rowId={(row) => row.applicationId}
            renderToolbar={(table) => (
              <DataTableToolbar table={table} searchPlaceholder="Search conversations…" />
            )}
            renderRowActions={(row) => (
              <RowActions
                label={row.applicantName || "this candidate"}
                primary={{
                  label: openId === row.applicationId ? "Hide conversation" : "Read conversation",
                  icon: MessageSquare,
                  onSelect: () => toggle(row.applicationId, row.unread),
                }}
                items={[
                  {
                    label: "Open candidate",
                    link: {
                      to: "/hiring/applications/$applicationId",
                      params: { applicationId: row.applicationId },
                    },
                  },
                ]}
              />
            )}
          />
        </>
      )}
    </>
  );
}
